import { AuditAction, AuditTarget } from "@soft-db/monitoring-api-client";
import { warn } from "vue";

export const useAuditNameResolver = createSharedComposable(() => {
	const { t } = useI18n();

	const fields = computed<Partial<Record<string, string>>>(() => ({
		// Common
		name: t("common.name"),
		description: t("common.description"),
		type: t("common.type"),
		sites: t("object.site"),
		"coordinates.latitude": t("common.latitude"),
		"coordinates.longitude": t("common.longitude"),
		timezone: t("common.timezone"),
		"recipients.users": t("common.userRecipients"),
		"recipients.emails": t("common.emailRecipients"),
		"recipients.phoneNumbers": t("common.phoneNumberRecipients"),
		emailRecipients: t("common.emailRecipients"),
		phoneNumberRecipients: t("common.phoneNumberRecipients"),
		email: t("common.email"),
		phoneNumber: t("common.phoneNumber"),
		identifier: t("common.identifier"),
		to: t("dashboard.to"),
		from: t("dashboard.from"),
		value: t("common.value"),
		internalRecipients: t("common.recipients"),
		externalRecipients: t("common.externalRecipients"),

		// Object
		group: t("common.group"),
		project: t("object.project"),
		site: t("object.project"),
		stations: t("object.station"),
		distributor: t("object.distributor"),
		reports: t("object.report"),

		// Alert
		enabled: t("common.enabled"),
		conditionMode: t("alert-settings.conditionsComparison"),
		conditions: t("alert-settings.conditions"),
		"definition.query.query": t("common.data"),
		validityConditions: t("alert-settings.precondition"),
		silent: t("alert-settings.email"),
		sortOrder: t("common.order"),
		comparison: t("alert-settings.thresholdComparison"),
		threshold: t("alert-settings.threshold"),
		secondThreshold: t("alert-settings.thresholdUpper"),
		thresholdError: t("alert-settings.exceedanceThreshold"),
		thresholdWarning: t("alert-settings.warningThreshold"),
		start: t("dashboard.from"),
		end: t("dashboard.to"),
		weekdays: t("common.weekdays"),
		scheduler: t("alert-settings.scheduler"),
		rateLimiter: t("alert-settings.rateLimiter"),
		mode: t("alert-settings.rateLimiterMode"),
		period: t("common.period"),
		validityMode: t("alert-settings.precondition"),
		"rateLimiter.mode": `${t("alert-settings.rateLimiter")} - ${t("alert-settings.rateLimiterMode")}`,
		"rateLimiter.period": `${t("alert-settings.rateLimiter")} - ${t("common.period")}`,

		// Project
		archived: t("project-settings.archived"),
		imageUrl: t("common.image"),

		// Vibration Criteria
		points: t("project-settings.vibrationCriteriaEntry"),
		x: "X",
		y: "Y",

		// Safe limit
		"query.query": t("common.data"),

		// Dynamic Image
		"image.id": t("common.image"),

		// Weekly Limit
		entries: t("common.points"),

		// Station
		rate: t("subscription-settings.subscriptionRates"),
		statusDataType: t("station.statusDataType"),
		serialNumber: t("station.serialNumber"),
		subscribers: t("station.subscribers"),
		autoUpdateCoordinates: t("station-settings.autoUpdateGPS"),
		sensors: t("object.sensor"),

		// User
		"avatar.url": t("common.image"),
		avatarUrl: t("common.image"),
		logoUrl: t("common.image"),
		role: t("common.role"),
		"features.dashboard": t("common.dashboard"),
		"features.integrity": t("integrity-settings.integrity"),
		"features.audit": t("common.audit"),
		"identity.password": t("common.password"),
		"identity.email": t("common.email"),
		"identity.publicToken": t("client.visibility"),
		preferredLang: t("profile.language"),

		// Report
		title: t("common.title"),
	}));

	function resolveFieldsName(name: string): string {
		const label = fields.value[name];
		if (label == null) {
			warn(
				`Label "${name}" not found for audit. Consider adding it in audit-name-resolver.ts. Falling back to "${name}"`
			);
		}

		return label || name;
	}

	const actions = computed<Record<AuditAction, string>>(() => ({
		created: t("audit.created"),
		updated: t("audit.updated"),
		deleted: t("audit.deleted"),
	}));

	function resolveActionName(action: AuditAction): string {
		return actions.value[action];
	}

	const collectionActions = computed<Record<AuditAction, string>>(() => ({
		created: t("audit.added"),
		updated: t("audit.updated"),
		deleted: t("audit.removed"),
	}));

	function resolveCollectionActionName(action: AuditAction): string {
		return collectionActions.value[action];
	}

	const targets = computed<Record<AuditTarget | "client", string>>(() => ({
		distributor: t("object.distributor"),
		station: t("object.station"),
		site: t("object.site"),
		alert: t("object.alert"),
		group: t("object.group"),
		weekly_limit: t("common.weeklyLimit"),
		vibration_criteria: t("dashboard.vibrationCriteria"),
		safe_limit: t("dashboard.vibrationCriteria"),
		dynamic_image: t("dashboard.plugins.dynamicImage"),
		dashboard: t("common.dashboard"),
		user_account: t("object.user"),
		client: t("object.client"),
		space: t("object.space"),
		public_page: t("object.publicPage"),
		report: t("object.report"),
	}));

	function resolveTargetName(target: AuditTarget | "client"): string {
		return targets.value[target];
	}

	return { resolveFieldsName, resolveActionName, resolveCollectionActionName, resolveTargetName };
});
